// useDirectoryFilters - Filter and sort state for FP Directory
// Simplified version without pagination (all cards displayed)

import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getMarketForCountry } from '../../../constants/markets'
import type { FranchisePartner } from '../types'

export interface DirectoryFilters {
  search: string
  market: string
  region: string
  bdm: string
  fpStatus: string
}

interface UseDirectoryFiltersReturn {
  filters: DirectoryFilters
  updateFilter: (key: keyof DirectoryFilters, value: string) => void
  clearFilters: () => void
  filteredData: FranchisePartner[]
  hasActiveFilters: boolean
  isInitialized: boolean
}

export const useDirectoryFilters = (
  franchisePartners: FranchisePartner[]
): UseDirectoryFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize filter state from URL parameters (default fpStatus to 'Active')
  const getInitialFilters = (): DirectoryFilters => ({
    search: searchParams.get('search') || '',
    market: searchParams.get('market') || '',
    region: searchParams.get('region') || '',
    bdm: searchParams.get('bdm') || '',
    fpStatus: searchParams.get('fpStatus') || 'Active'
  })

  const [filters, setFilters] = useState<DirectoryFilters>(getInitialFilters())

  // Update URL parameters when filters change
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true)
      return
    }

    const params = new URLSearchParams()

    if (filters.search) params.set('search', filters.search)
    if (filters.market) params.set('market', filters.market)
    if (filters.region) params.set('region', filters.region)
    if (filters.bdm) params.set('bdm', filters.bdm)
    if (filters.fpStatus) params.set('fpStatus', filters.fpStatus)

    setSearchParams(params, { replace: true })
  }, [filters, isInitialized, setSearchParams])

  // Filter franchise partners
  const filteredData = useMemo(() => {
    const filtered = franchisePartners.filter(fp => {
      // Search filter (franchise name, FP code, or contact names)
      const searchMatch = !filters.search ||
        fp.franchise_name.toLowerCase().includes(filters.search.toLowerCase()) ||
        fp.fp_code.toLowerCase().includes(filters.search.toLowerCase()) ||
        fp.franchisee_first_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        fp.franchisee_last_name?.toLowerCase().includes(filters.search.toLowerCase())

      // Market filter
      const fpMarket = fp.market || getMarketForCountry(fp.country)
      const marketMatch = !filters.market || fpMarket === filters.market

      // Region filter
      const regionMatch = !filters.region || fp.region_code === filters.region

      // ROM/FBC (BDM) filter
      const bdmMatch = !filters.bdm || fp.bdm_id === parseInt(filters.bdm, 10)

      // FP Status filter
      const fpStatusMatch = !filters.fpStatus || fp.fp_status === filters.fpStatus

      return searchMatch && marketMatch && regionMatch && bdmMatch && fpStatusMatch
    })

    // Sort alphabetically by franchise name
    return filtered.sort((a, b) => a.franchise_name.localeCompare(b.franchise_name))
  }, [franchisePartners, filters])

  const hasActiveFilters =
    filters.search !== '' ||
    filters.market !== '' ||
    filters.region !== '' ||
    filters.bdm !== '' ||
    filters.fpStatus !== ''

  const updateFilter = (key: keyof DirectoryFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      market: '',
      region: '',
      bdm: '',
      fpStatus: ''
    })
  }

  return {
    filters,
    updateFilter,
    clearFilters,
    filteredData,
    hasActiveFilters,
    isInitialized
  }
}
